<template>
<div class="sc-outer-container">
    <ControlGroup v-if="responsive">
          <Dropdown label="Policy" :options="relevantPolicyTypes.map(pt => ({ label: pt, value: pt }))" v-model="selectedPolicyType" reset-on-invalid small/>
        </ControlGroup>
        <ScorecardNav
          @click="click"
          :selectedIndex="selectedIndex"
          :slidesLength="slidesLength"
          :responsive="responsive"
        />
    <div class="sc-wrapper">
     <PolicyScorecard
     v-if="!responsive"
          layout="region"
          :regions="regions"
          :topic="topic"
          :family="family"
          :policies="relevantPolicies"
          @select:region="(region) => setRegion(region)"
          :test="test"
        />
      <div class="sc-container" ref="scContainer">
        <div class="scorecards">
          <flickity
          :ref="`flickity${topic}`"
          :key="`flickity${topic}`"
          :options="flktyOptions"
          class="scorecards-inner"
          v-if="!responsive && relevantPolicies.length > 0"
        >
          <template v-for="family in relevantFamilies">
            <PolicyScorecard
              v-for="policyType in family.policyTypes"
              :key="`${family.name}${policyType}`"
              :url="url"
              layout="policyType"
              :topic="topic"
              :family="family.name"
              :policyType="policyType"
              :regions="regions"
              :policies="relevantPolicies"
              :test="test"
            />
          </template>
        </flickity>
        <template v-if="responsive && relevantPolicies.length > 0">
        <PolicyScorecard
          :url="url"
          layout="combined"
          :topic="topic"
          :family="family"
          :policyType="selectedPolicyType"
          :regions="regions"
          :policies="relevantPolicies"
          :test="test"
        />
        </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ControlGroup, Dropdown } from '@international-energy-agency/vue-ui'
import Flickity from "vue-flickity";
import ScorecardNav from "./ScorecardNav.vue";
import PolicyScorecard from "./scorecard/PolicyScorecard.vue";
import tags from "../assets/tags.json";
import g20 from '@/assets/g20.json';
import members from '@/assets/members.json';
const policyTypesByFamily = tags.reduce((byFamily, tag) => {
  if (!tag.family) return byFamily;
  if (!byFamily[tag.family]) byFamily[tag.family] = [];
  if (!byFamily[tag.family].includes(tag.policyType))
    byFamily[tag.family].push(tag.policyType);
  return byFamily;
}, {});
const relevantStatus = ['In force', 'Announced', 'Achieved']
export default {
  name: "PolicyScorecards",
  props: {
    url: {
      type: String,
      required: true,
      default: "https://api.test.iea.org/policies/v2",
    },
    family: {
      type: String,
      required: true,
    },
    topic: {
      type: String,
      required: true,
    },
    policies: {
      type: Array,
      required: true,
    },
    setRegion: {
      type: Function,
      required: true,
    },
    test: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    ControlGroup,
    Dropdown,
    Flickity,
    ScorecardNav,
    PolicyScorecard,
  },
  data() {
    return {
      policyTypesByFamily,
      selectedIndex: 0,
      slidesLength: 10,
      regions: [...g20, ...members].sort(),
      relevantPolicies: [],
      selectedPolicyType: null,
      flktyOptions: {
        contain: true,
        prevNextButtons: false,
        pageDots: false,
        cellAlign: "left",
        selectedAttraction: 0.15,
        friction: 0.8,
        draggable: ">1",
      },
      screenWidth: null
    };
  },
  mounted() {
    this.setPolicies(this.policies)
     this.screenWidth = window.innerWidth;
  },
  beforeUnmount() {
    this.$refs[`flickity${this.topic}`].destroy();
  },
  watch: {
    topic(newTopic, oldTopic) {
      this.$refs[`flickity${oldTopic}`].destroy();
    },
    policies(newPolicies) {
      this.setPolicies(newPolicies)
    }
  },
  computed: {
    responsive() {
      return this.screenWidth < 960;
    },
    relevantFamilies() {
      const { policyTypesByFamily, relevantPolicyTypes } = this;
      return Object.keys(policyTypesByFamily).map((familyName) => ({
        name: familyName,
        policyTypes: policyTypesByFamily[familyName].filter((pt) =>
          relevantPolicyTypes.includes(pt)
        ),
      }));
    },
    relevantPolicyTypes() {
      const { topic, family, relevantPolicies } = this;
      const policyTypes = tags
        .reduce(
          (policyTypes, tag) =>
            tag.topic === topic &&
            (family === "All" || tag.family === family) &&
            !policyTypes.includes(tag.policyType)
              ? [...policyTypes, tag.policyType]
              : policyTypes,
          []
        )
        .sort(
          (a, b) =>
            tags.findIndex((tag) => tag.policyType === a) -
            tags.findIndex((tag) => tag.policyType === b)
        );
        return policyTypes.filter(policyType => relevantPolicies.some(policy => policy.type.includes(policyType)))
    },
    relevantCategories() {
      const { topic, relevantPolicyTypes } = this;
      return tags.reduce(
        (categories, tag) =>
          relevantPolicyTypes.includes(tag.policyType) &&
          tag.topic === topic &&
          !categories.includes(tag.category)
            ? [...categories, tag.category]
            : categories,
        []
      );
    },
  },
  methods: {
     setPolicies(policies) {
      const { regions } = this;
      this.relevantPolicies = policies.filter(policy => regions.includes(policy.country) && relevantStatus.includes(policy.status));
    },
    setHighlighted(value) {
      this.$emit("highlight", value);
    },
    click(dir) {
      const { topic, responsive } = this;
      if (responsive) {
        const container = this.$refs.scContainer;
        if (dir === 'next') {
          container.scrollLeft += 120;
        } else {
          container.scrollLeft -= 120;
        }
      } else {
      const flickity = this.$refs[`flickity${topic}`];
      dir === "next" ? flickity.next() : flickity.previous();
      this.selectedIndex = flickity.selectedIndex();
      }
    },
  },
};
</script>

<style>
.scorecards-inner {
  width: 100%;
}
.sc-loader {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}
.sc-wrapper {
  display: flex;
  margin-top: 12px;
  width: 100%;
}
.sc-container {
  position: relative;
  display: flex;
  visibility: visible;
}
.scorecards {
  position: absolute;
  left: 0;
  top: 0;
  display: flex;
  max-width: 1072px;
  width: calc(100vw - 400px);
  height: 100%;
}

@media screen and (max-width: 959px) {
 .scorecards {
  position: relative;
  width: auto;
  margin-left: 0px;
 }   
 .sc-container {
  width: auto;
  overflow-x: auto;
 }
}
</style>

